import { useState, useRef, useCallback, useEffect } from 'react'
import {
  addYoutubeVideos,
  getYoutubeVideo,
  deleteYoutubeVideo,
  chatWithYoutubeVideo,
  getYoutubeSession
} from '../api.js'
import { useJobs } from '../jobs'
import { useIsMobile } from '../useMediaQuery'

/* ─────────────────────────────────────────────────────────────────────────────
 * YoutubePanel.jsx
 *
 * All AI calls have been moved to the <bac>am</bac>kend (https://ollama.adaptix.in via
 * rag_service.py). This component:
 *   - Submits URLs to POST /youtube/videos  (backend fetches transcript + generates notes)
 *   - Polls GET /youtube/videos/{id}        until status === "ready"
 *   - Posts chat messages to POST /youtube/videos/{id}/chat
 *
 * No direct calls to Ollama or Anthropic remain in this file.
 * ───────────────────────────────────────────────────────────────────────────── */

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function extractVideoId (url) {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/
  ]
  for (const p of patterns) {
    const m = url.trim().match(p)
    if (m) return m[1]
  }
  return null
}

function getThumbnail (videoId) {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
}

/* ── Markdown renderer ───────────────────────────────────────────────────── */
function inlineFormat (text) {
  return text
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\* (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^(📌|💡|⚡|🔑)(.+)$/gm, '<div class="callout">$1$2</div>')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/^(?!<[hul]|<div|<hr)(.+)$/gm, '<p>$1</p>')
    .replace(/<p><\/p>/g, '')
}

function renderMarkdown (text) {
  if (!text) return null
  const lines = text.split('\n')
  const els = []
  let i = 0
  while (i < lines.length) {
    const l = lines[i]
    if (l.startsWith('## '))
      els.push(
        <h3
          key={i}
          style={{
            fontWeight: 700,
            fontSize: 14,
            margin: '12px 0 4px',
            color: 'var(--ink)'
          }}
        >
          {l.slice(3)}
        </h3>
      )
    else if (l.startsWith('# '))
      els.push(
        <h2
          key={i}
          style={{
            fontWeight: 700,
            fontSize: 16,
            margin: '14px 0 6px',
            color: 'var(--ink)'
          }}
        >
          {l.slice(2)}
        </h2>
      )
    else if (l.startsWith('### '))
      els.push(
        <h4
          key={i}
          style={{
            fontWeight: 600,
            fontSize: 13,
            margin: '8px 0 3px',
            color: 'var(--ink-2)'
          }}
        >
          {l.slice(4)}
        </h4>
      )
    else if (l.startsWith('- ') || l.startsWith('• ') || l.startsWith('* '))
      els.push(
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 8,
            padding: '2px 0',
            alignItems: 'flex-start'
          }}
        >
          <span
            style={{
              color: 'var(--rose)',
              flexShrink: 0,
              marginTop: 1,
              fontSize: 15
            }}
          >
            ·
          </span>
          <span
            style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-2)' }}
            dangerouslySetInnerHTML={{ __html: inlineFormat(l.slice(2)) }}
          />
        </div>
      )
    else if (l.match(/^\d+\.\s/)) {
      const content = l.replace(/^\d+\.\s/, '')
      const num = l.match(/^(\d+)/)[1]
      els.push(
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 9,
            padding: '2px 0',
            alignItems: 'flex-start'
          }}
        >
          <span
            style={{
              color: 'white',
              background: 'var(--rose)',
              fontWeight: 700,
              flexShrink: 0,
              fontSize: 10,
              borderRadius: '50%',
              width: 17,
              height: 17,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 3
            }}
          >
            {num}
          </span>
          <span
            style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-2)' }}
            dangerouslySetInnerHTML={{ __html: inlineFormat(content) }}
          />
        </div>
      )
    } else if (l.trim() === '---')
      els.push(
        <hr
          key={i}
          style={{
            border: 'none',
            borderTop: '1px solid var(--border)',
            margin: '8px 0'
          }}
        />
      )
    else if (
      l.startsWith('📌') ||
      l.startsWith('💡') ||
      l.startsWith('⚡') ||
      l.startsWith('🔑') ||
      l.startsWith('🎬') ||
      l.startsWith('🎯')
    )
      els.push(
        <div
          key={i}
          style={{
            background: 'rgba(239,68,68,0.07)',
            borderLeft: '3px solid var(--rose)',
            borderRadius: '0 var(--radius) var(--radius) 0',
            padding: '8px 12px',
            margin: '6px 0',
            fontSize: 13,
            lineHeight: 1.65
          }}
          dangerouslySetInnerHTML={{ __html: inlineFormat(l) }}
        />
      )
    else if (l.trim())
      els.push(
        <p
          key={i}
          style={{
            fontSize: 13,
            lineHeight: 1.75,
            color: 'var(--ink-2)',
            margin: '2px 0'
          }}
          dangerouslySetInnerHTML={{ __html: inlineFormat(l) }}
        />
      )
    else els.push(<div key={i} style={{ height: 4 }} />)
    i++
  }
  return els
}

/* ── PDF Export ──────────────────────────────────────────────────────────── */
function exportNotesPDF (video) {
  const pw = window.open('', '_blank')
  if (!pw) return
  const mdToHTML = (t = '') =>
    t
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h2>$1</h2>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/^\* (.+)$/gm, '<li>$1</li>')
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>')
      .replace(/^---$/gm, '<hr>')
      .replace(/^(📌|💡|⚡|🔑|🎬|🎯)(.+)$/gm, '<div class="callout">$1$2</div>')
      .replace(/\n{2,}/g, '</p><p>')
      .replace(/^(?!<[hul]|<div|<hr)(.+)$/gm, '<p>$1</p>')
      .replace(/<p><\/p>/g, '')

  pw.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>${video.title || 'YouTube Notes'}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:Georgia,serif;font-size:13px;line-height:1.75;color:#1a1a2e;padding:48px 56px;max-width:800px;margin:0 auto}
  .header{border-bottom:3px solid #ef4444;padding-bottom:16px;margin-bottom:28px}
  .header h1{font-size:20px;font-weight:700;color:#1a1a2e;margin-bottom:4px}
  .header .url{font-size:11px;color:#ef4444;font-family:'Courier New',monospace;margin-bottom:2px}
  .header .meta{font-size:11px;color:#6b7280;font-family:'Courier New',monospace}
  h2{font-size:15px;font-weight:700;margin:14px 0 4px;color:#1a1a2e}
  h3{font-size:13.5px;font-weight:600;margin:8px 0 3px;color:#1a1a2e}
  p{margin:2px 0;color:#374151}
  ul,ol{padding-left:20px}
  li{margin:3px 0;color:#374151}
  code{background:#f3f4f6;padding:1px 5px;border-radius:3px;font-family:'Courier New',monospace;font-size:11.5px}
  .callout{background:#fff5f5;border-left:3px solid #ef4444;padding:8px 12px;margin:6px 0;border-radius:0 4px 4px 0}
  hr{border:none;border-top:1px solid #e5e7eb;margin:10px 0}
  strong{color:#111827;font-weight:600}
  em{color:#6b7280}
  .footer{margin-top:40px;padding-top:14px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;display:flex;justify-content:space-between}
  @media print{body{padding:32px 40px}@page{margin:20mm}}
</style></head><body>
  <div class="header">
    <h1>${video.title || 'YouTube Video Notes'}</h1>
    <div class="url">youtube.com/watch?v=${video.id}</div>
    <div class="meta">Generated by StudyMind AI · ${new Date().toLocaleDateString(
      'en-IN',
      { year: 'numeric', month: 'long', day: 'numeric' }
    )}</div>
  </div>
  <div>${mdToHTML(video.notes)}</div>
  <div class="footer"><span>StudyMind AI — YouTube Notes</span><span>${
    video.title || video.id
  }</span></div>
</body></html>`)
  pw.document.close()
  pw.onload = () => {
    pw.focus()
    pw.print()
  }
}

/* ── VideoCard ───────────────────────────────────────────────────────────── */
function VideoCard ({ video, isActive, onClick, onRemove }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '9px 10px',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid',
        borderColor: isActive ? 'rgba(239,68,68,0.4)' : 'var(--border)',
        background: isActive ? 'rgba(239,68,68,0.06)' : 'var(--surface-2)',
        cursor: 'pointer',
        transition: 'all var(--transition)',
        position: 'relative'
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: 54,
          height: 36,
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
          flexShrink: 0,
          position: 'relative',
          background: 'var(--surface-3)'
        }}
      >
        <img
          src={getThumbnail(video.id)}
          alt=''
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
          onError={e => {
            e.target.style.display = 'none'
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.25)'
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 16,
              color: 'white',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            play_circle
          </span>
        </div>
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            color: isActive ? 'var(--rose, #ef4444)' : 'var(--ink-2)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.3,
            marginBottom: 3
          }}
        >
          {video.title || video.id}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {video.status === 'loading' && (
            <span
              style={{
                fontSize: 10,
                color: 'var(--amber)',
                display: 'flex',
                alignItems: 'center',
                gap: 3
              }}
            >
              <div
                className='spinner spinner-sm'
                style={{
                  borderTopColor: 'var(--amber)',
                  borderColor: 'rgba(245,158,11,0.2)',
                  width: 9,
                  height: 9,
                  borderWidth: 1.5
                }}
              />
              Processing…
            </span>
          )}
          {video.status === 'ready' && (
            <span
              style={{
                fontSize: 10,
                color: 'var(--green)',
                display: 'flex',
                alignItems: 'center',
                gap: 3
              }}
            >
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 12, fontVariationSettings: "'FILL' 1" }}
              >
                check_circle
              </span>
              Notes ready
            </span>
          )}
          {video.status === 'error' && (
            <span style={{ fontSize: 10, color: 'var(--rose)' }}>⚠ Error</span>
          )}
          {video.status === 'idle' && (
            <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>Queued</span>
          )}
        </div>
      </div>

      {/* Remove btn */}
      <button
        onClick={e => {
          e.stopPropagation()
          onRemove()
        }}
        className='btn btn-ghost btn-icon-sm'
        style={{ flexShrink: 0 }}
      >
        <span className='material-symbols-rounded' style={{ fontSize: 14 }}>
          close
        </span>
      </button>
    </div>
  )
}

/* ── ChatMessage ─────────────────────────────────────────────────────────── */
function ChatMessage ({ msg }) {
  if (msg.role === 'user')
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 12
        }}
      >
        <div className='chat-bubble-user' style={{ maxWidth: '78%' }}>
          {msg.content}
        </div>
      </div>
    )
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        marginBottom: 14,
        alignItems: 'flex-start'
      }}
      className='animate-fadeIn'
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          flexShrink: 0,
          marginTop: 2,
          background:
            'linear-gradient(135deg, var(--rose, #ef4444), var(--orange, #f97316))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(239,68,68,0.3)'
        }}
      >
        <span
          className='material-symbols-rounded'
          style={{
            fontSize: 15,
            color: 'white',
            fontVariationSettings: "'FILL' 1"
          }}
        >
          smart_toy
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className='chat-bubble-ai' style={{ overflow: 'hidden' }}>
          {msg.streaming && !msg.content ? (
            <div style={{ display: 'flex', gap: 5, padding: '3px 0' }}>
              <div className='typing-dot' />
              <div className='typing-dot' />
              <div className='typing-dot' />
            </div>
          ) : (
            <div className='md-content'>{renderMarkdown(msg.content)}</div>
          )}
          {msg.streaming && msg.content && (
            <span
              style={{
                display: 'inline-block',
                width: 2,
                height: 13,
                background: 'var(--rose)',
                marginLeft: 2,
                animation: 'pulse 0.8s infinite',
                verticalAlign: 'middle'
              }}
            />
          )}
        </div>
        {!msg.streaming && (
          <div
            style={{
              fontSize: 10.5,
              color: 'var(--ink-3)',
              marginTop: 3,
              paddingLeft: 2
            }}
          >
            {new Date().toLocaleTimeString('en-IN', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Main Component ──────────────────────────────────────────────────────── */
const POLL_INTERVAL_MS = 2500

export default function YoutubePanel () {
  const isMobile = useIsMobile()
  const [urlInput, setUrlInput] = useState('')
  const [videos, setVideos] = useState([]) // [{id, title, notes, status, chatHistory, has_transcript}]
  const [activeIdx, setActiveIdx] = useState(null)
  const [activeTab, setActiveTab] = useState('notes') // "notes" | "chat"
  const [chatDraft, setChatDraft] = useState('')
  const [chatSending, setChatSending] = useState(false)
  const [exporting, setExporting] = useState(false)
  const chatBottomRef = useRef(null)
  const textRef = useRef(null)
  const pollTimers = useRef({}) // videoId → intervalId
  const { notify } = useJobs()

  const activeVideo = activeIdx !== null ? videos[activeIdx] : null

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [activeVideo?.chatHistory, chatSending])

  /* ── Cleanup all polls on unmount ────────────────────────────────────── */
  useEffect(() => {
    return () => {
      Object.values(pollTimers.current).forEach(clearInterval)
    }
  }, [])

  /* ── Poll backend until video is ready ───────────────────────────────── */
  const startPolling = useCallback(
    videoId => {
      if (pollTimers.current[videoId]) return // already polling

      const timer = setInterval(async () => {
        try {
          const data = await getYoutubeVideo(videoId)

          if (data.status === 'ready') {
            clearInterval(pollTimers.current[videoId])
            delete pollTimers.current[videoId]

            setVideos(prev =>
              prev.map(v =>
                v.id === videoId
                  ? {
                      ...v,
                      title: data.title || videoId,
                      notes: data.notes || '',
                      status: 'ready',
                      has_transcript: data.has_transcript ?? false
                    }
                  : v
              )
            )
            notify?.({
              tone: 'success',
              icon: 'check_circle',
              title: 'YouTube notes generated',
              desc: data.title || videoId
            })
          }
          // status === "processing" → keep polling
        } catch (err) {
          clearInterval(pollTimers.current[videoId])
          delete pollTimers.current[videoId]
          setVideos(prev =>
            prev.map(v =>
              v.id === videoId
                ? {
                    ...v,
                    status: 'error',
                    notes: `⚠️ Failed to load video: ${err.message}`
                  }
                : v
            )
          )
        }
      }, POLL_INTERVAL_MS)

      pollTimers.current[videoId] = timer
    },
    [notify]
  )

  /* ── Hydrate from backend session on mount ───────────────────────────────
   * Videos are processed server-side, so navigating away never stops them.
   * On return we reload the session and resume polling anything still running. */
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await getYoutubeSession()
        if (cancelled || !data?.videos?.length) return
        const list = data.videos.map(v => ({
          id: v.video_id,
          title: v.title || v.video_id,
          notes: v.notes || '',
          status: v.notes ? 'ready' : 'loading',
          chatHistory: [],
          has_transcript: v.has_transcript ?? false
        }))
        setVideos(prev => (prev.length ? prev : list))
        setActiveIdx(prev => (prev === null && list.length ? 0 : prev))
        list.forEach(v => {
          if (v.status === 'loading') startPolling(v.id)
        })
      } catch {
        /* no session yet — ignore */
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ── Add URLs → submit to backend ────────────────────────────────────── */
  const handleAddUrls = useCallback(async () => {
    const lines = urlInput.split(/[\n,\s]+/).filter(Boolean)
    const newIds = []

    for (const raw of lines) {
      const id = extractVideoId(raw)
      if (!id) continue
      if (videos.some(v => v.id === id)) continue
      newIds.push(id)
    }

    if (!newIds.length) return

    // Optimistically add placeholder entries
    const placeholders = newIds.map(id => ({
      id,
      title: id,
      notes: '',
      status: 'loading',
      chatHistory: [],
      has_transcript: false
    }))

    setVideos(prev => {
      const merged = [...prev, ...placeholders]
      return merged
    })
    setUrlInput('')

    if (activeIdx === null) setActiveIdx(videos.length)

    // Submit to backend — backend fetches transcript + generates notes via Ollama
    try {
      const urls = newIds.map(id => `https://www.youtube.com/watch?v=${id}`)
      await addYoutubeVideos(urls)
    } catch (err) {
      // Mark all as errored if the submission itself fails
      setVideos(prev =>
        prev.map(v =>
          newIds.includes(v.id) && v.status === 'loading'
            ? {
                ...v,
                status: 'error',
                notes: `⚠️ Submission failed: ${err.message}`
              }
            : v
        )
      )
      return
    }

    // Start polling each new video
    newIds.forEach(id => startPolling(id))
  }, [urlInput, videos, activeIdx, startPolling])

  /* ── Remove video ────────────────────────────────────────────────────── */
  const handleRemove = useCallback(
    async (videoId, idx) => {
      clearInterval(pollTimers.current[videoId])
      delete pollTimers.current[videoId]

      try {
        await deleteYoutubeVideo(videoId)
      } catch {
        /* best-effort */
      }

      setVideos(prev => prev.filter((_, j) => j !== idx))
      if (activeIdx === idx) setActiveIdx(videos.length > 1 ? 0 : null)
      else if (activeIdx > idx) setActiveIdx(prev => prev - 1)
    },
    [activeIdx, videos.length]
  )

  /* ── Chat — uses backend /youtube/videos/{id}/chat ───────────────────── */
  const handleChatSend = useCallback(
    async text => {
      if (!activeVideo || chatSending || !text.trim()) return

      const userMsg = { role: 'user', content: text }
      const currentIdx = activeIdx

      setVideos(prev => {
        const next = [...prev]
        next[currentIdx] = {
          ...next[currentIdx],
          chatHistory: [
            ...(next[currentIdx].chatHistory || []),
            userMsg,
            { role: 'assistant', content: '', streaming: true }
          ]
        }
        return next
      })
      setChatSending(true)

      const history = (activeVideo.chatHistory || []).map(m => ({
        role: m.role,
        content: m.content
      }))

      try {
        // All AI logic lives in backend/youtube_service.py → rag_service._generate()
        // which calls Ollama at https://ollama.adaptix.in
        const data = await chatWithYoutubeVideo(activeVideo.id, text, history)
        const reply = data.response || "I couldn't generate a response."

        setVideos(prev => {
          const next = [...prev]
          const hist = [...(next[currentIdx].chatHistory || [])]
          hist[hist.length - 1] = {
            role: 'assistant',
            content: reply,
            streaming: false
          }
          next[currentIdx] = { ...next[currentIdx], chatHistory: hist }
          return next
        })
      } catch (err) {
        setVideos(prev => {
          const next = [...prev]
          const hist = [...(next[currentIdx].chatHistory || [])]
          hist[hist.length - 1] = {
            role: 'assistant',
            content: `⚠️ Something went wrong: ${err.message}`,
            streaming: false
          }
          next[currentIdx] = { ...next[currentIdx], chatHistory: hist }
          return next
        })
      } finally {
        setChatSending(false)
      }
    },
    [activeVideo, activeIdx, chatSending]
  )

  const submitChat = () => {
    const t = chatDraft.trim()
    if (!t) return
    setChatDraft('')
    if (textRef.current) textRef.current.style.height = 'auto'
    handleChatSend(t)
  }

  /* ── Render ──────────────────────────────────────────────────────────── */
  const QUICK_QUESTIONS = [
    'Summarize the main ideas',
    'What are the key takeaways?',
    'Explain the most important concept',
    'What examples were given?'
  ]

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bg)'
      }}
    >
      {/* ── Header bar ── */}
      <div
        style={{
          padding: '10px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            flexShrink: 0,
            background: 'rgba(239,68,68,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(239,68,68,0.25)'
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 17,
              color: 'var(--rose, #ef4444)',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            smart_display
          </span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>YouTube Notes</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            Paste video links → get AI notes → chat with the video
          </div>
        </div>

        {/* Export PDF — only when notes exist */}
        {activeVideo?.status === 'ready' && activeTab === 'notes' && (
          <button
            onClick={() => {
              setExporting(true)
              setTimeout(() => {
                exportNotesPDF(activeVideo)
                setExporting(false)
              }, 100)
            }}
            disabled={exporting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 13px',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(34,197,94,0.1)',
              border: '1px solid rgba(34,197,94,0.3)',
              color: 'var(--green, #22c55e)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all var(--transition)'
            }}
          >
            {exporting ? (
              <div
                className='spinner spinner-sm'
                style={{
                  borderTopColor: 'var(--green)',
                  borderColor: 'rgba(34,197,94,0.2)'
                }}
              />
            ) : (
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}
              >
                picture_as_pdf
              </span>
            )}
            Export PDF
          </button>
        )}
      </div>

      {/* ── Body: left sidebar + right content ── */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden',
          minWidth: 0
        }}
      >
        {/* ── Left: URL input + video list ── */}
        <div
          style={{
            width: isMobile ? '100%' : 264,
            borderRight: isMobile ? 'none' : '1px solid var(--border)',
            borderBottom: isMobile ? '1px solid var(--border)' : 'none',
            maxHeight: isMobile ? '42vh' : 'none',
            background: 'var(--surface)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flexShrink: 0,
            padding: '14px 12px',
            gap: 10
          }}
        >
          {/* URL input */}
          <div
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border-2)',
              borderRadius: 'var(--radius-lg)',
              padding: '10px 12px'
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--ink-3)',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.06em'
              }}
            >
              Add YouTube Links
            </div>
            <textarea
              value={urlInput}
              onChange={e => setUrlInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleAddUrls()
                }
              }}
              placeholder={'https://youtu.be/...\nPaste one or more links'}
              rows={3}
              style={{
                width: '100%',
                background: 'var(--surface-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: '7px 10px',
                fontSize: 12,
                color: 'var(--ink)',
                resize: 'none',
                outline: 'none',
                fontFamily: 'var(--font-mono)',
                lineHeight: 1.5,
                marginBottom: 8
              }}
            />
            <button
              onClick={handleAddUrls}
              disabled={!urlInput.trim()}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 'var(--radius-md)',
                background: urlInput.trim()
                  ? 'linear-gradient(135deg, var(--rose, #ef4444), var(--orange, #f97316))'
                  : 'var(--surface-3)',
                border: 'none',
                color: urlInput.trim() ? 'white' : 'var(--ink-3)',
                fontSize: 12.5,
                fontWeight: 600,
                cursor: urlInput.trim() ? 'pointer' : 'default',
                transition: 'all var(--transition)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                boxShadow: urlInput.trim()
                  ? '0 3px 12px rgba(239,68,68,0.3)'
                  : 'none',
                opacity: !urlInput.trim() ? 0.5 : 1
              }}
            >
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}
              >
                add_circle
              </span>
              Add & Generate
            </button>
          </div>

          {/* Video list */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}
          >
            {videos.length === 0 ? (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: '20px 10px',
                  textAlign: 'center'
                }}
              >
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 36,
                    color: 'var(--ink-3)',
                    fontVariationSettings: "'FILL' 0"
                  }}
                >
                  smart_display
                </span>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--ink-3)',
                    lineHeight: 1.6
                  }}
                >
                  Paste YouTube links above to get started
                </div>
              </div>
            ) : (
              videos.map((v, i) => (
                <VideoCard
                  key={v.id}
                  video={v}
                  isActive={i === activeIdx}
                  onClick={() => {
                    setActiveIdx(i)
                    setActiveTab('notes')
                  }}
                  onRemove={() => handleRemove(v.id, i)}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Right: notes / chat ── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {!activeVideo ? (
            /* Empty state */
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 20,
                padding: 40,
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'rgba(239,68,68,0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(239,68,68,0.2)'
                }}
              >
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 40,
                    color: 'var(--rose, #ef4444)',
                    fontVariationSettings: "'FILL' 1"
                  }}
                >
                  smart_display
                </span>
              </div>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                  Add YouTube videos
                </h2>
                <p
                  style={{
                    color: 'var(--ink-3)',
                    fontSize: 13,
                    lineHeight: 1.6,
                    maxWidth: 340
                  }}
                >
                  Paste one or multiple YouTube URLs on the left. The AI will
                  extract the transcript and generate study notes — then you can
                  chat with the video.
                </p>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  justifyContent: 'center'
                }}
              >
                {[
                  '🎬 Auto transcript',
                  '📝 Smart notes',
                  '💬 Ask questions',
                  '📄 Export PDF'
                ].map(f => (
                  <span
                    key={f}
                    style={{
                      padding: '6px 12px',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 12,
                      color: 'var(--ink-2)'
                    }}
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Video info strip */}
              <div
                style={{
                  padding: '10px 18px',
                  borderBottom: '1px solid var(--border)',
                  background: 'var(--surface)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexShrink: 0
                }}
              >
                {/* Thumbnail */}
                <div
                  style={{
                    width: 64,
                    height: 42,
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    flexShrink: 0,
                    background: 'var(--surface-3)',
                    position: 'relative'
                  }}
                >
                  <img
                    src={getThumbnail(activeVideo.id)}
                    alt=''
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: 'var(--ink)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {activeVideo.title}
                  </div>
                  <a
                    href={`https://youtube.com/watch?v=${activeVideo.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                      fontSize: 11,
                      color: 'var(--rose, #ef4444)',
                      textDecoration: 'none'
                    }}
                  >
                    youtube.com/watch?v={activeVideo.id} ↗
                  </a>
                </div>

                {/* Notes / Chat tabs */}
                <div style={{ display: 'flex', gap: 4 }}>
                  {[
                    { id: 'notes', icon: 'description', label: 'Notes' },
                    { id: 'chat', icon: 'chat', label: 'Chat' }
                  ].map(t => (
                    <button
                      key={t.id}
                      onClick={() => setActiveTab(t.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '6px 11px',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 12.5,
                        fontWeight: 500,
                        color:
                          activeTab === t.id ? 'var(--ink)' : 'var(--ink-3)',
                        background:
                          activeTab === t.id
                            ? 'var(--surface-3)'
                            : 'transparent',
                        border: '1px solid',
                        borderColor:
                          activeTab === t.id
                            ? 'var(--border-2)'
                            : 'transparent',
                        transition: 'all var(--transition)'
                      }}
                    >
                      <span
                        className='material-symbols-rounded'
                        style={{
                          fontSize: 15,
                          color:
                            activeTab === t.id
                              ? 'var(--rose, #ef4444)'
                              : 'inherit',
                          fontVariationSettings:
                            activeTab === t.id ? "'FILL' 1" : "'FILL' 0"
                        }}
                      >
                        {t.icon}
                      </span>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Notes tab ── */}
              {activeTab === 'notes' && (
                <div
                  style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}
                >
                  {activeVideo.status === 'loading' && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 300,
                        gap: 16
                      }}
                    >
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          background:
                            'linear-gradient(135deg, var(--rose, #ef4444), var(--orange, #f97316))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 20px rgba(239,68,68,0.35)'
                        }}
                      >
                        <span
                          className='material-symbols-rounded'
                          style={{
                            fontSize: 28,
                            color: 'white',
                            fontVariationSettings: "'FILL' 1"
                          }}
                        >
                          auto_awesome
                        </span>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 15,
                            marginBottom: 6
                          }}
                        >
                          Generating notes…
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                          Fetching transcript and analysing content
                        </div>
                      </div>
                      <div className='ai-indicator animate-fadeIn'>
                        <span
                          className='material-symbols-rounded'
                          style={{
                            fontSize: 15,
                            fontVariationSettings: "'FILL' 1"
                          }}
                        >
                          smart_toy
                        </span>
                        Processing video
                        <div className='dots'>
                          <div className='dot' />
                          <div className='dot' />
                          <div className='dot' />
                        </div>
                      </div>
                    </div>
                  )}

                  {activeVideo.status === 'ready' && (
                    <>
                      {/* Notes header */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 16,
                          padding: '9px 12px',
                          background: 'var(--surface-2)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-lg)'
                        }}
                      >
                        <span
                          className='material-symbols-rounded'
                          style={{
                            fontSize: 15,
                            color: 'var(--rose, #ef4444)',
                            fontVariationSettings: "'FILL' 1"
                          }}
                        >
                          auto_awesome
                        </span>
                        <span
                          style={{
                            flex: 1,
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: 'var(--ink)'
                          }}
                        >
                          AI Notes — {activeVideo.title}
                        </span>
                        {activeVideo.has_transcript ? (
                          <span
                            className='badge badge-green'
                            style={{ fontSize: 10 }}
                          >
                            <span
                              className='material-symbols-rounded'
                              style={{ fontSize: 12 }}
                            >
                              check_circle
                            </span>
                            Transcript used
                          </span>
                        ) : (
                          <span
                            className='badge badge-amber'
                            style={{ fontSize: 10 }}
                          >
                            <span
                              className='material-symbols-rounded'
                              style={{ fontSize: 12 }}
                            >
                              info
                            </span>
                            No transcript
                          </span>
                        )}
                      </div>
                      {/* Notes content */}
                      <div
                        style={{
                          background: 'var(--surface-2)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-lg)',
                          padding: '16px 18px'
                        }}
                      >
                        {renderMarkdown(activeVideo.notes)}
                      </div>
                    </>
                  )}

                  {activeVideo.status === 'error' && (
                    <div
                      style={{
                        padding: '16px',
                        background: 'var(--rose-soft)',
                        border: '1px solid rgba(244,63,94,0.25)',
                        borderRadius: 'var(--radius-lg)',
                        color: 'var(--rose)',
                        fontSize: 13
                      }}
                    >
                      {activeVideo.notes}
                    </div>
                  )}
                </div>
              )}

              {/* ── Chat tab ── */}
              {activeTab === 'chat' && (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}
                >
                  {/* Messages area */}
                  <div
                    style={{
                      flex: 1,
                      overflowY: 'auto',
                      padding: '20px 20px 8px'
                    }}
                  >
                    {(!activeVideo.chatHistory ||
                      activeVideo.chatHistory.length === 0) &&
                      !chatSending && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 280,
                            gap: 20,
                            textAlign: 'center'
                          }}
                        >
                          <div
                            style={{
                              width: 56,
                              height: 56,
                              borderRadius: '50%',
                              background:
                                'linear-gradient(135deg, var(--rose, #ef4444), var(--orange, #f97316))',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 20px rgba(239,68,68,0.3)'
                            }}
                          >
                            <span
                              className='material-symbols-rounded'
                              style={{
                                fontSize: 28,
                                color: 'white',
                                fontVariationSettings: "'FILL' 1"
                              }}
                            >
                              smart_toy
                            </span>
                          </div>
                          <div>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 16,
                                marginBottom: 6
                              }}
                            >
                              Chat with the video
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: 'var(--ink-3)',
                                maxWidth: 340,
                                lineHeight: 1.6
                              }}
                            >
                              Ask anything about{' '}
                              <strong style={{ color: 'var(--ink-2)' }}>
                                {activeVideo.title}
                              </strong>
                              .
                              {activeVideo.status === 'loading'
                                ? ' Notes are still generating…'
                                : ' AI answers based on the video content.'}
                            </div>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 7,
                              justifyContent: 'center',
                              maxWidth: 420
                            }}
                          >
                            {QUICK_QUESTIONS.map(q => (
                              <button
                                key={q}
                                onClick={() => handleChatSend(q)}
                                disabled={activeVideo.status === 'loading'}
                                style={{
                                  padding: '7px 13px',
                                  borderRadius: 'var(--radius-full)',
                                  border: '1px solid var(--border-2)',
                                  background: 'var(--surface)',
                                  color: 'var(--ink-2)',
                                  fontSize: 12,
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  transition: 'all var(--transition)',
                                  opacity:
                                    activeVideo.status === 'loading' ? 0.4 : 1
                                }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.borderColor =
                                    'var(--rose)'
                                  e.currentTarget.style.color = 'var(--rose)'
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.borderColor =
                                    'var(--border-2)'
                                  e.currentTarget.style.color = 'var(--ink-2)'
                                }}
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                    {(activeVideo.chatHistory || []).map((msg, i) => (
                      <ChatMessage key={i} msg={msg} />
                    ))}

                    {/* {chatSending && (
                      <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: "50%",
                          background: "linear-gradient(135deg, var(--rose, #ef4444), var(--orange, #f97316))",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <span className="material-symbols-rounded" style={{ fontSize: 15, color: "white", fontVariationSettings: "'FILL' 1" }}>smart_toy</span>
                        </div>
                        <div className="chat-bubble-ai" style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
                          <div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" />
                        </div>
                      </div>
                    )} */}
                    <div ref={chatBottomRef} />
                  </div>

                  {/* Chat input */}
                  <div
                    style={{
                      padding: '12px 16px',
                      borderTop: '1px solid var(--border)',
                      background: 'var(--surface)',
                      flexShrink: 0
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'flex-end',
                        background: 'var(--surface-3)',
                        border: '1px solid var(--border-2)',
                        borderRadius: 'var(--radius-xl)',
                        padding: '8px 10px 8px 14px',
                        transition: 'border-color var(--transition)'
                      }}
                      onFocusCapture={e =>
                        (e.currentTarget.style.borderColor =
                          'rgba(239,68,68,0.5)')
                      }
                      onBlurCapture={e =>
                        (e.currentTarget.style.borderColor = 'var(--border-2)')
                      }
                    >
                      <textarea
                        ref={textRef}
                        value={chatDraft}
                        onChange={e => {
                          setChatDraft(e.target.value)
                          e.target.style.height = 'auto'
                          e.target.style.height =
                            Math.min(e.target.scrollHeight, 140) + 'px'
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            submitChat()
                          }
                        }}
                        placeholder={
                          activeVideo.status === 'loading'
                            ? 'Notes still generating, but you can ask anyway…'
                            : `Ask about ${activeVideo.title}…`
                        }
                        rows={1}
                        disabled={chatSending}
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: 'none',
                          resize: 'none',
                          outline: 'none',
                          padding: 0,
                          fontSize: 13,
                          lineHeight: 1.5,
                          color: 'var(--ink)',
                          minHeight: 24,
                          fontFamily: 'var(--font-sans)',
                          maxHeight: 140,
                          overflowY: 'auto'
                        }}
                      />
                      <button
                        onClick={submitChat}
                        disabled={!chatDraft.trim() || chatSending}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 9,
                          flexShrink: 0,
                          background:
                            chatDraft.trim() && !chatSending
                              ? 'var(--rose, #ef4444)'
                              : 'var(--surface-2)',
                          border: '1px solid',
                          borderColor:
                            chatDraft.trim() && !chatSending
                              ? 'var(--rose, #ef4444)'
                              : 'var(--border-2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all var(--transition)',
                          cursor:
                            chatDraft.trim() && !chatSending
                              ? 'pointer'
                              : 'default',
                          boxShadow:
                            chatDraft.trim() && !chatSending
                              ? '0 2px 8px rgba(239,68,68,0.3)'
                              : 'none'
                        }}
                      >
                        {chatSending ? (
                          <div className='spinner spinner-sm' />
                        ) : (
                          <span
                            className='material-symbols-rounded'
                            style={{
                              fontSize: 17,
                              color: chatDraft.trim()
                                ? 'white'
                                : 'var(--ink-3)',
                              fontVariationSettings: "'FILL' 1"
                            }}
                          >
                            send
                          </span>
                        )}
                      </button>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 6,
                        fontSize: 10.5,
                        color: 'var(--ink-3)',
                        padding: '0 2px'
                      }}
                    >
                      <span>Enter to send · Shift+Enter for newline</span>
                      <span>
                        {
                          (activeVideo.chatHistory || []).filter(
                            m => m.role === 'user'
                          ).length
                        }{' '}
                        messages
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
